import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, TrendingUp, BarChart3, ArrowDownRight, ArrowUpRight, Database, AlertTriangle, Building2, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import AIPageInsight from "@/components/AIPageInsight";
import { PeriodBadge } from "@/components/Formatters";
import TargetManager from "@/components/TargetManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null || isNaN(n)) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };
const monthLabel = (ym) => { if (!ym) return '--'; const [y, m] = ym.split('-'); const names = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${names[parseInt(m)]} ${y}`; };

export default function TargetAchievement() {
  const { period, refDate, token, currentMonthInfo } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (refDate) params.ref_date = refDate;
      const [targetRes, execRes] = await Promise.all([
        axios.get(`${API}/kpi/targets`, { headers: { Authorization: `Bearer ${token}` }, params }),
        axios.get(`${API}/kpi/executive`, { headers: { Authorization: `Bearer ${token}` }, params: { period, ...params } }),
      ]);
      setData({ ...targetRes.data, exec: execRes.data });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, period, refDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const d = data || {};
  const sel = d.selected || {};
  const exec = d.exec || {};
  const projects = d.project_breakdown || [];
  const timeline = d.monthly_breakdown || [];
  const fySummary = d.fy_summary || {};
  const hasData = d.has_data;
  const selectedMonth = d.selected_month;
  const collTarget = exec.collection_target || 0;
  const collActual = exec.collection || 0;
  const collEff = exec.collection_efficiency || 0;

  if (!hasData && !loading) return (
    <div className="space-y-5" data-testid="target-achievement-page">
      <div><h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Target vs Achievement</h1><p className="text-xs sm:text-sm text-slate-500 mt-0.5">Monthly target analysis with carry-forward tracking</p></div>
      <div className="empty-state"><Database className="h-12 w-12 text-slate-300 mb-4" /><p className="text-base font-medium text-slate-500">No Target Data for This Period</p><p className="text-sm text-slate-400 mt-1">Upload Target Table via Data Management</p></div>
    </div>
  );

  // For MTD: use month-level data (sel). For QTD/YTD: use period-cumulative (exec)
  const isMtd = (data?.exec?.period === 'mtd');
  const targetVal = isMtd ? (sel.target || 0) : (exec.target_value || sel.target || 0);
  const carryIn = isMtd ? (sel.carry_in || 0) : (exec.carry_in_val || 0);
  const effTarget = isMtd ? (sel.effective_target || 0) : (exec.effective_target_value || targetVal);
  const actualSales = exec.net_sales || sel.actual || 0;
  const achPct = effTarget > 0 ? Math.round(actualSales / effTarget * 1000) / 10 : (sel.achievement_pct || 0);
  const gap = effTarget - actualSales;
  const salesCount = exec.sales_count || sel.sales_count || 0;
  const cancelCount = exec.cancel_count || sel.cancel_count || 0;
  const targetCount = isMtd ? (sel.target_count || 0) : 0;

  return (
    <div className="space-y-5" data-testid="target-achievement-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Target vs Achievement</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
          Monthly target analysis with carry-forward tracking
          {d.fy_label && <span className="ml-2 font-medium text-blue-600">{d.fy_label} {d.quarter_label}</span>}
        </p>
      </div>

        {/* Selected Month Badge */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1E293B] text-white" data-testid="selected-month-badge">
            <Target className="h-4 w-4" />
            <span className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>{monthLabel(selectedMonth)}</span>
          </div>
          {fySummary.achievement_pct > 0 && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
              FY Achievement: <span className={`font-bold ${fySummary.achievement_pct >= 80 ? 'text-emerald-600' : fySummary.achievement_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{fySummary.achievement_pct}%</span>
              <span className="text-slate-400 mx-1">|</span>
              Target: {fmt(fySummary.total_target)} &middot; Actual: {fmt(fySummary.total_actual)}
            </div>
          )}
        </div>

        <AIPageInsight token={token} period={"ytd"} refDate={refDate} context={`Month: ${monthLabel(selectedMonth)}, Target: ${fmt(sel.target)}, Actual: ${fmt(sel.actual)}, Achievement: ${achPct}%, Carry-in: ${fmt(carryIn)}, Effective target: ${fmt(effTarget)}, FY achievement: ${fySummary.achievement_pct}%, FY target: ${fmt(fySummary.total_target)}, FY actual: ${fmt(fySummary.total_actual)}`} pageTitle="Target Achievement" />

        {/* KPIs — period-aware */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 sm:gap-3" data-testid="month-kpis">
          <div className="kpi-card" data-testid="kpi-target">
            <div className="flex items-center gap-2 mb-1.5"><Target className="h-4 w-4 text-blue-500" /><span className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider">{isMtd ? 'Month Target' : 'Sales Target'}</span></div>
            <p className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{fmt(targetVal)}</p>
            {targetCount > 0 && <p className="text-[10px] text-slate-500 mt-1">{targetCount} units</p>}
          </div>
          <div className="kpi-card" data-testid="kpi-carry-in">
            <div className="flex items-center gap-2 mb-1.5">
              <ArrowDownRight className={`h-4 w-4 ${carryIn > 0 ? 'text-amber-500' : 'text-emerald-500'}`} />
              <span className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider">Carry-In</span>
            </div>
            <p className={`text-lg sm:text-xl font-bold ${carryIn > 0 ? 'text-amber-600' : 'text-emerald-600'}`} style={{ fontFamily: 'Manrope' }}>{carryIn > 0 ? fmt(carryIn) : 'Nil'}</p>
            <p className="text-[10px] text-slate-500 mt-1">{carryIn > 0 ? 'Deficit from prior' : 'No deficit'}</p>
          </div>
          <div className="kpi-card" data-testid="kpi-effective-target">
            <div className="flex items-center gap-2 mb-1.5"><BarChart3 className="h-4 w-4 text-indigo-500" /><span className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider">Eff. Target</span></div>
            <p className="text-lg sm:text-xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope' }}>{fmt(effTarget)}</p>
            <p className="text-[10px] text-slate-500 mt-1">{carryIn > 0 ? `${fmt(targetVal)} + ${fmt(carryIn)}` : 'No carry'}</p>
          </div>
          <div className="kpi-card" data-testid="kpi-net-actual">
            <div className="flex items-center gap-2 mb-1.5"><TrendingUp className="h-4 w-4 text-emerald-500" /><span className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider">Net Actual</span></div>
            <p className="text-lg sm:text-xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope' }}>{fmt(actualSales)}</p>
            <p className="text-[10px] text-slate-500 mt-1">{salesCount} sales, {cancelCount} cancels</p>
          </div>
          <div className="kpi-card col-span-2 sm:col-span-1" data-testid="kpi-achievement-pct">
            <div className="flex items-center gap-2 mb-1.5">
              {achPct >= 100 ? <ArrowUpRight className="h-4 w-4 text-emerald-500" /> : <Target className={`h-4 w-4 ${achPct >= 70 ? 'text-amber-500' : 'text-red-500'}`} />}
              <span className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider">Achievement</span>
            </div>
            <p className={`text-lg sm:text-xl font-bold ${achPct >= 90 ? 'text-emerald-600' : achPct >= 70 ? 'text-amber-600' : 'text-red-600'}`} style={{ fontFamily: 'Manrope' }}>{achPct}%</p>
            <p className="text-[10px] text-slate-500 mt-1">{achPct >= 100 ? 'Exceeded!' : gap > 0 ? `Gap: ${fmt(gap)}` : '--'}</p>
          </div>
        </div>

        {/* Collection Target vs Actual (period-aware) */}
        {collTarget > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3 p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100" data-testid="coll-target-summary">
            <div>
              <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">Collection Target</p>
              <p className="text-lg font-bold text-emerald-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(collTarget)}</p>
            </div>
            <div>
              <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">Actual Collection</p>
              <p className="text-lg font-bold text-emerald-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(collActual)}</p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">Collection Efficiency</p>
              <p className={`text-lg font-bold mt-0.5 ${collEff >= 100 ? 'text-emerald-600' : collEff >= 80 ? 'text-amber-600' : 'text-red-600'}`} style={{ fontFamily: 'Manrope' }}>{collEff}%</p>
            </div>
          </div>
        )}
      {/* Carry-Forward Alert */}
      {carryIn > 0 && (
        <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50 border border-amber-200" data-testid="carry-forward-alert">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500" />
          <div>
            <p className="text-xs font-bold text-amber-800 uppercase tracking-wider">Carry-Forward Deficit</p>
            <p className="text-sm text-slate-700 mt-0.5">
              {fmt(carryIn)} deficit carried into {monthLabel(selectedMonth)}. Effective target = {fmt(sel.target)} (base) + {fmt(carryIn)} (carry) = {fmt(effTarget)}.
            </p>
          </div>
        </div>
      )}

      {/* Project-wise Breakdown */}
      {projects.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="project-breakdown">
          <CardHeader className="pb-2 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#1E5FAD]" />
              <CardTitle className="text-sm font-semibold text-slate-900">Project-wise Target vs Actual — {monthLabel(selectedMonth)}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="project-breakdown-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 font-semibold text-slate-600">Project</th>
                    <th className="text-right py-2 px-2 font-semibold text-blue-600">Target</th>
                    <th className="text-right py-2 px-2 font-semibold text-emerald-600">Actual</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Variance</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Ach %</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Units (T/A)</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, i) => (
                    <tr key={p.project_name} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-slate-50/50' : ''}`}>
                      <td className="py-2 px-2 font-medium text-slate-800 max-w-[180px] truncate">{p.project_name}</td>
                      <td className="py-2 px-2 text-right text-blue-600">{fmt(p.target)}</td>
                      <td className="py-2 px-2 text-right text-emerald-600 font-medium">{fmt(p.actual)}</td>
                      <td className={`py-2 px-2 text-right font-medium ${p.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{p.variance >= 0 ? '+' : ''}{fmt(p.variance)}</td>
                      <td className="py-2 px-2 text-right">
                        <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${p.achievement_pct >= 90 ? 'bg-emerald-100 text-emerald-700' : p.achievement_pct >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {p.achievement_pct}%
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right text-slate-500">{p.target_count} / {p.actual_count}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-100/70 font-bold">
                    <td className="py-2 px-2 text-slate-800">Total</td>
                    <td className="py-2 px-2 text-right text-blue-700">{fmt(sel.target)}</td>
                    <td className="py-2 px-2 text-right text-emerald-700">{fmt(sel.actual)}</td>
                    <td className={`py-2 px-2 text-right ${(sel.actual - sel.target) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{(sel.actual - sel.target) >= 0 ? '+' : ''}{fmt((sel.actual || 0) - (sel.target || 0))}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${achPct >= 90 ? 'bg-emerald-100 text-emerald-700' : achPct >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {achPct}%
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right text-slate-500">{sel.target_count || 0} / {sel.actual_count || 0}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project Bar Chart */}
      {projects.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="project-chart">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900">Project Target vs Actual — {monthLabel(selectedMonth)}</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <ResponsiveContainer width="100%" height={Math.max(220, projects.length * 28)}>
              <BarChart data={projects.slice(0, 12)} layout="vertical" barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={fmt} />
                <YAxis type="category" dataKey="project_name" tick={{ fontSize: 10, fill: '#475569' }} width={140} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={v => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="target" fill="#CBD5E1" name="Target" radius={[0, 4, 4, 0]} barSize={12} />
                <Bar dataKey="actual" name="Actual" radius={[0, 4, 4, 0]} barSize={12}>
                  {projects.slice(0, 12).map((p) => <Cell key={p.project} fill={p.achievement_pct >= 90 ? '#10B981' : p.achievement_pct >= 70 ? '#F59E0B' : '#EF4444'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* FY Monthly Timeline */}
      {timeline.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="fy-monthly-timeline">
          <CardHeader className="pb-2 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-900">{d.fy_label} — Monthly Carry-Forward Timeline</CardTitle>
              <div className="text-[10px] text-slate-400 flex items-center gap-2">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> On Track</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> At Risk</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Behind</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="fy-timeline-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 font-semibold text-slate-600">Month</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Target</th>
                    <th className="text-right py-2 px-2 font-semibold text-amber-600">Carry In</th>
                    <th className="text-right py-2 px-2 font-semibold text-indigo-600">Eff. Target</th>
                    <th className="text-right py-2 px-2 font-semibold text-emerald-600">Actual</th>
                    <th className="text-right py-2 px-2 font-semibold text-red-600">Carry Out</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Ach %</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.map((mb, i) => {
                    const isSelected = mb.month === selectedMonth;
                    return (
                      <tr key={mb.month} className={`border-b border-slate-100 transition-colors ${isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : i % 2 === 0 ? 'bg-slate-50/50' : ''}`}>
                        <td className={`py-2 px-2 font-medium ${isSelected ? 'text-blue-800 flex items-center gap-1' : 'text-slate-700'}`}>
                          {isSelected && <ChevronRight className="h-3 w-3 text-blue-500" />}
                          {monthLabel(mb.month)}
                        </td>
                        <td className="py-2 px-2 text-right text-slate-600">{fmt(mb.target)}</td>
                        <td className="py-2 px-2 text-right text-amber-600 font-medium">{mb.carry_in > 0 ? fmt(mb.carry_in) : '-'}</td>
                        <td className="py-2 px-2 text-right text-indigo-600 font-medium">{fmt(mb.effective_target)}</td>
                        <td className="py-2 px-2 text-right text-emerald-600 font-medium">{fmt(mb.actual)}</td>
                        <td className={`py-2 px-2 text-right font-medium ${mb.carry_out > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{mb.carry_out > 0 ? fmt(mb.carry_out) : '-'}</td>
                        <td className="py-2 px-2 text-right">
                          <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${mb.achievement_pct >= 90 ? 'bg-emerald-100 text-emerald-700' : mb.achievement_pct >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                            {mb.achievement_pct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-100/70 font-bold">
                    <td className="py-2 px-2 text-slate-800">FY Total</td>
                    <td className="py-2 px-2 text-right text-blue-700">{fmt(fySummary.total_target)}</td>
                    <td className="py-2 px-2 text-right text-amber-600">-</td>
                    <td className="py-2 px-2 text-right text-indigo-600">-</td>
                    <td className="py-2 px-2 text-right text-emerald-700">{fmt(fySummary.total_actual)}</td>
                    <td className="py-2 px-2 text-right text-red-700">{fySummary.total_carry_forward > 0 ? fmt(fySummary.total_carry_forward) : '-'}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${fySummary.achievement_pct >= 90 ? 'bg-emerald-100 text-emerald-700' : fySummary.achievement_pct >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {fySummary.achievement_pct}%
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Trend Chart */}
      {timeline.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="monthly-trend-chart">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900">{d.fy_label} — Monthly Target vs Actual</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={timeline} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={ym => { const [,m] = ym.split('-'); const n = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return n[parseInt(m)]; }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={fmt} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={v => fmt(v)} labelFormatter={monthLabel} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="target" fill="#CBD5E1" name="Target" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" name="Net Actual" radius={[4, 4, 0, 0]}>
                  {timeline.map((mb) => <Cell key={mb.month} fill={mb.month === selectedMonth ? '#1E5FAD' : mb.achievement_pct >= 90 ? '#10B981' : mb.achievement_pct >= 70 ? '#F59E0B' : '#EF4444'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Manual Target Entry — Admin/Manager only */}
      <TargetManager token={token} targetType="sales" />
    </div>
  );
}
